import { IremboTransition, point } from '../workflow/models';

export class WorkflowLayoutCalculator {
  private HORIZONTAL_SPACING = 200;
  private VERTICAL_SPACING = 100;
  private TRANSITION_WIDTH = 100;
  private TRANSITION_HEIGHT = 60;
  private RFA_VERTICAL_OFFSET = 150;
  private INITIAL_X = 100;
  private INITIAL_Y = 300;
  private STATE_TO_TRANSITION_SPACING = 150;

  private statePositions: Map<string, point> = new Map();
  private processedStates: Set<string> = new Set();
  private maxY = this.INITIAL_Y;

  calculateLayout(workflow: IremboTransition[]): IremboTransition[] {
    this.statePositions.clear();
    this.processedStates.clear();
    this.maxY = this.INITIAL_Y;

    const initialState = this.findInitialState(workflow);
    if (initialState) {
      this.processState(workflow, initialState, 0, 0);
    } else {
      console.error("Couldn't find an initial state");
    }

    return workflow;
  }

  private findInitialState(workflow: IremboTransition[]): string | undefined {
    const allEndStates = new Set(
      workflow.flatMap(t =>
        [t.endStateOne?.stateCode, t.endStateTwo?.stateCode].filter(Boolean)
      )
    );
    return workflow.find(t => !allEndStates.has(t.startState))?.startState;
  }

  private processState(
    workflow: IremboTransition[],
    stateCode: string,
    level: number,
    verticalOffset: number
  ): point {
    if (this.processedStates.has(stateCode)) {
      return this.statePositions.get(stateCode)!;
    }
    this.processedStates.add(stateCode);

    const statePosition = this.getStatePosition(
      stateCode,
      level,
      verticalOffset
    );
    const stateTransitions = workflow.filter(t => t.startState === stateCode);

    let subLevel = 0;
    stateTransitions.forEach(transition => {
      const transitionPosition = this.getTransitionPosition(
        statePosition,
        transition.event,
        subLevel
      );
      transition.position = transitionPosition;

      if (transition.endStateOne) {
        const isRFA = transition.event === 'REQUEST_FOR_ACTION';
        const endStateOnePosition = this.processEndState(
          workflow,
          transition.endStateOne.stateCode,
          level + 1,
          isRFA
            ? verticalOffset + this.RFA_VERTICAL_OFFSET * (subLevel + 1)
            : verticalOffset
        );
        transition.endStateOne.position = endStateOnePosition;
      }

      if (transition.endStateTwo) {
        const endStateTwoPosition = this.processEndState(
          workflow,
          transition.endStateTwo.stateCode,
          level + 1,
          verticalOffset - this.VERTICAL_SPACING
        );
        transition.endStateTwo.position = endStateTwoPosition;
      }

      subLevel++;
    });

    return statePosition;
  }

  private processEndState(
    workflow: IremboTransition[],
    stateCode: string,
    level: number,
    verticalOffset: number
  ): point {
    return this.processState(workflow, stateCode, level, verticalOffset);
  }

  private getStatePosition(
    stateCode: string,
    level: number,
    verticalOffset: number
  ): point {
    if (this.statePositions.has(stateCode)) {
      return this.statePositions.get(stateCode)!;
    }
    const position = {
      x: this.INITIAL_X + level * this.HORIZONTAL_SPACING,
      y: this.INITIAL_Y + verticalOffset,
    };
    this.statePositions.set(stateCode, position);
    this.maxY = Math.max(this.maxY, position.y);
    return position;
  }

  private getTransitionPosition(
    statePosition: point,
    eventType: string,
    subLevel: number
  ): point {
    const position = {
      x: statePosition.x + this.STATE_TO_TRANSITION_SPACING,
      y: statePosition.y + subLevel * this.VERTICAL_SPACING,
    };
    this.maxY = Math.max(this.maxY, position.y);
    return position;
  }
}
